import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { WidgetConfig } from "./types";
import { getStrings } from "./i18n";
import { ChatInterface } from "./ChatInterface";

interface Props {
  config: WidgetConfig;
}

export function MrWineWidget({ config }: Props) {
  const [open, setOpen] = useState(false);
  const t = getStrings(config.lang);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const positionClass = `mw-${config.position}`;

  return (
    <div
      className={`mw-root mw-${config.theme} ${positionClass}`}
      style={{ ["--mw-accent" as string]: config.accentColor }}
      lang={config.lang}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            className="mw-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-label={t.title}
          >
            <div className="mw-header">
              <div className="mw-header-left">
                <span className="mw-dot" aria-hidden="true" />
                <span className="mw-title">{t.title}</span>
              </div>
              <button
                type="button"
                className="mw-close"
                aria-label={t.close}
                onClick={() => setOpen(false)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ChatInterface config={config} />
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        className="mw-bubble"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t.close : t.open}
        aria-expanded={open}
      >
        {open ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>
    </div>
  );
}
